import { Left, PlayList, Right } from '@tunasong/icons'
import {
  Button,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuContent,
  MenuItem,
  MenuTrigger,
} from '@tunasong/ui-lib'
import type { FC, ReactNode } from 'react'
import { setPosition } from './set-position.js'
import styles from './set-navigation.module.css'

export interface SetNavigationItem {
  id: string
  name: string
  /** "D minor · ♩ 118": what the performer needs to know before the next song starts */
  meta?: string
  icon?: ReactNode
}

export interface SetNavigationProps {
  /** The landmark name: "Set", or "Playlist" */
  label: string
  /** "song" or "track" */
  noun: string
  items: SetNavigationItem[]
  index: number
  /** Item 0 is the set's overview, so the songs count from 1 after it */
  hasOverview?: boolean
  onSelect: (index: number) => void
  /** Phones: ‹ n / N ›, with Previous and Next as icons that keep their song names */
  compact?: boolean
}

const capitalize = (s: string) => `${s.charAt(0).toUpperCase()}${s.slice(1)}`

/**
 * The set, at the far right of the bottom bar (away from Play, GH #1612): Previous, the position that opens a
 * jump list, and Next. Next is the one raised button. Previous and Next are named with their song, and are left out
 * at the ends of the set rather than shown disabled.
 */
export const SetNavigation: FC<SetNavigationProps> = props => {
  const { label, noun, items, index, hasOverview = false, onSelect, compact = false } = props

  if (items.length <= 1) {
    return null
  }

  const position = setPosition({ index, count: items.length, hasOverview, noun: capitalize(noun) })
  const previous = index > 0 ? items[index - 1] : undefined
  const next = index < items.length - 1 ? items[index + 1] : undefined
  const previousIsOverview = hasOverview && index === 1
  const previousLabel = previousIsOverview ? 'Previous: Overview' : `Previous ${noun}: ${previous?.name ?? ''}`
  const nextLabel = `Next ${noun}: ${next?.name ?? ''}`

  return (
    <nav
      aria-label={label}
      className={[styles.nav, compact ? styles.compact : undefined, 'tuna-hide-print'].filter(Boolean).join(' ')}
    >
      {previous && compact ? (
        <IconButton className={styles.target} aria-label={previousLabel} onClick={() => onSelect(index - 1)}>
          <Left />
        </IconButton>
      ) : null}
      {previous && !compact ? (
        <Button
          variant="text"
          color="inherit"
          className={[styles.target, styles.previous].join(' ')}
          aria-label={previousLabel}
          startIcon={<Left />}
          onClick={() => onSelect(index - 1)}
        >
          <span className={styles.stack}>
            <span className={styles.caption}>Previous</span>
            <span className={styles.name}>{previousIsOverview ? 'Overview' : previous.name}</span>
          </span>
        </Button>
      ) : null}
      <Menu>
        <MenuTrigger
          render={
            <Button
              variant="outlined"
              color="inherit"
              className={[styles.target, styles.position].join(' ')}
              aria-label={`${position.long}, jump to a ${noun}`}
              endIcon={<PlayList />}
            >
              {position.short}
            </Button>
          }
        />
        <MenuContent>
          {items.map((item, idx) => (
            <MenuItem
              key={`${item.id}-${idx}`}
              /* The current item stays focusable: disabled would skip "you are here" for screen readers */
              aria-current={idx === index ? 'true' : undefined}
              className={styles.menuItem}
              onClick={() => (idx === index ? undefined : onSelect(idx))}
            >
              {item.icon ? <ListItemIcon>{item.icon}</ListItemIcon> : null}
              <ListItemText>{item.name}</ListItemText>
            </MenuItem>
          ))}
        </MenuContent>
      </Menu>
      {next && position.isOverview ? (
        <Button
          variant="contained"
          className={[styles.target, styles.next, styles.start].join(' ')}
          aria-label={`Start the set: ${next.name}`}
          endIcon={<Right />}
          onClick={() => onSelect(index + 1)}
        >
          <span className={styles.stack}>
            <span className={styles.startCaption}>{next.meta ? `Start the set · ${next.meta}` : 'Start the set'}</span>
            <span className={styles.name}>{next.name}</span>
          </span>
        </Button>
      ) : null}
      {next && !position.isOverview && compact ? (
        <IconButton
          className={[styles.target, styles.next].join(' ')}
          aria-label={nextLabel}
          onClick={() => onSelect(index + 1)}
        >
          <Right />
        </IconButton>
      ) : null}
      {next && !position.isOverview && !compact ? (
        <Button
          variant="text"
          color="inherit"
          className={[styles.target, styles.next].join(' ')}
          aria-label={nextLabel}
          endIcon={<Right />}
          onClick={() => onSelect(index + 1)}
        >
          <span className={styles.stack}>
            <span className={styles.caption}>{next.meta ? `Next · ${next.meta}` : 'Next'}</span>
            <span className={styles.name}>{next.name}</span>
          </span>
        </Button>
      ) : null}
    </nav>
  )
}

export default SetNavigation
